import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, Phone, Mail, ExternalLink, CheckCircle2, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import type { Resource, ResourceCategory } from '@/contexts/ResourceContext';

const categoryColors: Record<ResourceCategory, string> = {
  food: '#22c55e',
  shelter: '#8b5cf6',
  medical: '#ef4444',
  skills: '#3b82f6',
  tools: '#f59e0b',
  transport: '#06b6d4',
  legal: '#ec4899',
  childcare: '#f97316',
  elderlycare: '#6366f1',
  'mental-health': '#a855f7',
  community: '#14b8a6',
  other: '#6b7280',
};

const categoryLabels: Record<ResourceCategory, string> = {
  food: 'Food & Groceries',
  shelter: 'Shelter & Housing',
  medical: 'Health Care',
  skills: 'Skills & Learning',
  tools: 'Tools & Equipment',
  transport: 'Transportation',
  legal: 'Legal Aid',
  childcare: 'Childcare',
  elderlycare: 'Elder Care',
  'mental-health': 'Mental Health',
  community: 'Community',
  other: 'Other',
};

interface ResourceCardProps {
  resource: Resource;
  onSelect?: (resource: Resource) => void;
  variant?: 'default' | 'compact';
  className?: string;
}

export function ResourceCard({ resource, onSelect, variant = 'default', className = '' }: ResourceCardProps) {
  const [expanded, setExpanded] = useState(false);
  const color = categoryColors[resource.category];

  if (variant === 'compact') {
    return (
      <Card
        className={`group cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] border-0 shadow-md overflow-hidden ${className}`}
        onClick={() => onSelect?.(resource)}
      >
        <div className="flex items-start gap-3 p-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0 transition-transform group-hover:scale-110"
            style={{ backgroundColor: `${color}20` }}
          >
            <span style={{ filter: `drop-shadow(0 1px 1px ${color}40)` }}>
              {resource.category === 'food' && '🍎'}
              {resource.category === 'shelter' && '🏠'}
              {resource.category === 'medical' && '⚕️'}
              {resource.category === 'skills' && '📚'}
              {resource.category === 'tools' && '🔧'}
              {resource.category === 'transport' && '🚗'}
              {resource.category === 'legal' && '⚖️'}
              {resource.category === 'childcare' && '👶'}
              {resource.category === 'eldercare' && '👵'}
              {resource.category === 'mental-health' && '💜'}
              {resource.category === 'community' && '🌱'}
              {resource.category === 'other' && '✨'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm truncate group-hover:text-primary transition-colors">
              {resource.name}
            </h4>
            <div className="flex items-center gap-2 mt-1">
              <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground truncate">
                {resource.address.split(',')[0]}
              </span>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={`group cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-[1.02] border-0 shadow-md overflow-hidden ${className}`}
      onClick={() => onSelect?.(resource)}
    >
      {/* Category color bar */}
      <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${color}, ${color}80)` }} />

      <CardContent className="p-4 sm:p-5 space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-base sm:text-lg leading-tight truncate group-hover:text-primary transition-colors">
                {resource.name}
              </h3>
              {resource.verified && (
                <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 text-primary shrink-0" />
              )}
            </div>
            <Badge
              variant="secondary"
              className="text-xs font-normal"
              style={{
                backgroundColor: `${color}15`,
                color: color,
              }}
            >
              {categoryLabels[resource.category]}
            </Badge>
          </div>
          <div
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-lg sm:text-xl shrink-0 transition-transform group-hover:scale-110 group-hover:rotate-3"
            style={{ backgroundColor: `${color}15` }}
          >
            <span style={{ filter: `drop-shadow(0 2px 2px ${color}30)` }}>
              {resource.category === 'food' && '🍎'}
              {resource.category === 'shelter' && '🏠'}
              {resource.category === 'medical' && '⚕️'}
              {resource.category === 'skills' && '📚'}
              {resource.category === 'tools' && '🔧'}
              {resource.category === 'transport' && '🚗'}
              {resource.category === 'legal' && '⚖️'}
              {resource.category === 'childcare' && '👶'}
              {resource.category === 'eldercare' && '👵'}
              {resource.category === 'mental-health' && '💜'}
              {resource.category === 'community' && '🌱'}
              {resource.category === 'other' && '✨'}
            </span>
          </div>
        </div>

        {/* Description - shows full text when expanded */}
        <p className="text-sm text-muted-foreground leading-relaxed">
          {resource.description}
        </p>

        {/* Expand/Collapse button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-4 w-4" />
              <span>Show less</span>
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              <span>Show more</span>
            </>
          )}
        </button>

        {/* Details - always visible */}
        <div className="space-y-2 pt-2 border-t border-border/50">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 shrink-0" />
            <span className="truncate">{resource.address}</span>
          </div>
          {resource.hours && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4 shrink-0" />
              <span>{resource.hours}</span>
            </div>
          )}
        </div>

        {/* Tags */}
        {resource.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {resource.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 text-xs rounded-full bg-secondary text-secondary-foreground"
              >
                #{tag}
              </span>
            ))}
            {resource.tags.length > 3 && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-secondary text-secondary-foreground">
                +{resource.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Contact details - show when expanded */}
        {expanded && (
          <div className="space-y-2 pt-2 border-t border-border/50">
            {resource.contact?.email && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="h-4 w-4 shrink-0" />
                <span className="truncate">{resource.contact.email}</span>
              </div>
            )}
            {resource.contact?.phone && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-4 w-4 shrink-0" />
                <span>{resource.contact.phone}</span>
              </div>
            )}
            {resource.contact?.website && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ExternalLink className="h-4 w-4 shrink-0" />
                <span className="truncate">{resource.contact.website}</span>
              </div>
            )}
          </div>
        )}

        {/* Contact actions */}
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {resource.contact?.phone && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-9 min-w-[100px]"
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = `tel:${resource.contact!.phone}`;
              }}
            >
              <Phone className="h-4 w-4 mr-1" />
              Call
            </Button>
          )}
          {resource.contact?.website && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-9 min-w-[100px]"
              onClick={(e) => {
                e.stopPropagation();
                window.open(resource.contact!.website, '_blank');
              }}
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Website
            </Button>
          )}
          <Button
            size="sm"
            className="flex-1 h-9 gap-1 min-w-[100px]"
            style={{ backgroundColor: color }}
            onClick={(e) => {
              e.stopPropagation();
              onSelect?.(resource);
            }}
          >
            View in Graph
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
